import React, { useCallback } from 'react';
import { EmojiClickData } from 'emoji-picker-react';
import { RightSection, RightIcons, EmojiDiv, ReactionDiv } from './styles';
import IconButton from '../../../button/iconButton';
import EmojiPickerDropdown from '../../../emojiPickerDropdown';
import { Dropdown, DropdownItem } from '../../../dropdown';
import { ICON_BUTTON_SIZE, ICON_BUTTON_TYPE } from '../../../../global/constants';
import { MessageDetailsInterface } from '../../../../interfaces/chatMessageInterface';
import {
  useMessageActionsContext,
  useMessageSelectionContext,
  useMessageEnvironmentContext
} from '../MessageFeedContext';

interface MessageActionsProps {
  message: MessageDetailsInterface;
  index: number;
}

const MessageActions: React.FC<MessageActionsProps> = ({ message, index }) => {
  // Stable callbacks — MessageActions will not re-render just because
  // selection state (openEmoji, etc.) changes in other bubbles
  const {
    onReactOnMessage,
    onClickReply,
    editMessageData,
    openDeleteMessageModal,
    handleOpenChange,
    setSelectedMessage,
    setOpenEmoji
  } = useMessageActionsContext();

  // Volatile selection/UI state — re-renders only when selection changes
  const { openEmoji, selectedMesaage: selectedMessage, emojiLoading, emojiButtonRef } = useMessageSelectionContext();

  // Stable environment info — re-renders only when user/workspace/layout changes
  const { currentUser, userUseEmoji, mobile, isMessage } = useMessageEnvironmentContext();

  const currentUserId = currentUser?.id;
  const messageId = message?.id;

  const handleReactionToggle = useCallback(() => {
    if (!messageId) return;
    handleOpenChange(messageId);
  }, [handleOpenChange, messageId]);

  const handleQuickEmojiClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      const emoji = event.currentTarget.dataset.emoji;
      if (!emoji) return;
      onReactOnMessage({ emoji }, message);
    },
    [message, onReactOnMessage]
  );

  const handlePickerEmojiClick = useCallback(
    (emojiData: EmojiClickData) => {
      onReactOnMessage(emojiData, message);
    },
    [message, onReactOnMessage]
  );

  const handleReplyClick = useCallback(() => {
    onClickReply(message);
  }, [message, onClickReply]);

  const handleOpenQuickActions = useCallback(() => {
    if (!messageId) return;
    setSelectedMessage(messageId);
  }, [messageId, setSelectedMessage]);

  const handleCloseQuickActions = useCallback(() => {
    setSelectedMessage(null);
  }, [setSelectedMessage]);

  const handleEditMessage = useCallback(() => {
    editMessageData(message);
  }, [editMessageData, message]);

  const handleDeleteMessage = useCallback(() => {
    if (!messageId) return;
    openDeleteMessageModal(messageId);
  }, [messageId, openDeleteMessageModal]);

  return (
    <RightSection className='right-section'>
      <RightIcons>
        {Array.isArray(userUseEmoji) &&
          userUseEmoji.map((emojiData: string) => (
            <EmojiDiv
              role='button'
              key={`${message?.id}-emoji-${emojiData}`}
              data-emoji={emojiData}
              onClick={handleQuickEmojiClick}>
              {emojiData}
            </EmojiDiv>
          ))}
        <ReactionDiv role='button' ref={emojiButtonRef?.current[index]} onClick={handleReactionToggle}>
          <IconButton
            iconName='reaction-icon'
            size={mobile ? ICON_BUTTON_SIZE.EXTRA_LARGE : ICON_BUTTON_SIZE.SMALL}
            variant={ICON_BUTTON_TYPE.TERTIARY}
            type='rounded'
          />
          {openEmoji && selectedMessage === message?.id && (
            <EmojiPickerDropdown
              emojiButtonRef={emojiButtonRef?.current[index]}
              isOpenEmoji={openEmoji}
              setIsOpenEmoji={setOpenEmoji}
              setOpenDropdownId={setSelectedMessage}
              isMessage={isMessage}
              onEmojiClick={handlePickerEmojiClick}
              isEmojiLoading={emojiLoading}
            />
          )}
        </ReactionDiv>
        <IconButton
          iconName='reply-icon'
          size={mobile ? ICON_BUTTON_SIZE.EXTRA_LARGE : ICON_BUTTON_SIZE.SMALL}
          variant={ICON_BUTTON_TYPE.TERTIARY}
          type='rounded'
          onClick={handleReplyClick}
        />
        {currentUserId === message?.sender?.id && (
          <>
            <Dropdown
              activeClassName='active'
              modalTitle='Quick Action'
              content={
                <IconButton
                  iconName='three-dots-horizontal-icon'
                  size={mobile ? ICON_BUTTON_SIZE.EXTRA_LARGE : ICON_BUTTON_SIZE.SMALL}
                  variant={ICON_BUTTON_TYPE.TERTIARY}
                  onClick={handleOpenQuickActions}
                  type='rounded'
                />
              }
              trigger='click'
              onOutsideClick={handleCloseQuickActions}>
              <DropdownItem label='Edit' tone='primary' onClick={handleEditMessage} />
              <DropdownItem label='Delete' tone='critical' onClick={handleDeleteMessage} />
            </Dropdown>
          </>
        )}
      </RightIcons>
    </RightSection>
  );
};

export default React.memo(MessageActions);
